import React, { useEffect, useMemo, useState } from 'react';
import styles from './CreateOrderModal.module.css';
import { OrderStepper } from './OrderStepper';
import {
  ProductOption,
  CustomerOption,
} from '../ordersTypes';
import { useCustomersStore } from '../../../../store/customersStore';
import { useOrdersStore } from '../../../../store/ordersStore';
import { useSkusStore } from '../../../../store/skusStore';
import { useCategoriesStore } from '../../../../store/categoriesStore';
import { Customer } from '../../../../services/customersApi';
import { Sku } from '../../../../services/skusApi';

function skuToOption(s: Sku): ProductOption {
  return {
    id: String(s.id),
    name: s.product_name,
    category: s.category_name || 'Other',
    price: s.list_price ?? s.unit_cost,
    stock: s.total_stock,
  };
}

function customerToOption(c: Customer): CustomerOption {
  return {
    id: String(c.id),
    name: c.store_name || c.full_name,
    area: [c.city, c.state].filter(Boolean).join(', ') || '—',
    creditStatus: c.metrics.outstanding_balance === 0 ? 'Excellent' : c.metrics.outstanding_balance < c.metrics.total_revenue * 0.3 ? 'Good' : 'Fair',
  };
}

interface CartItem {
  product: ProductOption;
  qty: number;
}

interface CreateOrderModalProps {
  open: boolean;
  onClose: () => void;
}

/* ───── Step 0: Products ───── */
const StepProducts: React.FC<{
  cart: CartItem[];
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
}> = ({ cart, setCart }) => {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [search, setSearch] = useState('');

  const skus = useSkusStore((s) => s.items);
  const fetchSkus = useSkusStore((s) => s.fetchList);
  const setSkuFilters = useSkusStore((s) => s.setFilters);
  const cats = useCategoriesStore((s) => s.items);
  const fetchCats = useCategoriesStore((s) => s.fetchList);

  useEffect(() => {
    setSkuFilters({ status: 'active', in_stock_only: true });
    fetchSkus();
    fetchCats();
  }, [setSkuFilters, fetchSkus, fetchCats]);

  const productOptions = useMemo<ProductOption[]>(() => skus.map(skuToOption), [skus]);
  const categories = useMemo<string[]>(
    () => ['All', ...cats.map((c) => c.name)],
    [cats]
  );

  const filtered = useMemo(() => {
    return productOptions.filter(
      (p) =>
        (activeCategory === 'All' || p.category === activeCategory) &&
        p.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [productOptions, activeCategory, search]);

  const getQty = (id: string) => cart.find((c) => c.product.id === id)?.qty ?? 0;

  const addToCart = (product: ProductOption) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.product.id === product.id);
      if (existing) return prev.map((c) => c.product.id === product.id ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { product, qty: 1 }];
    });
  };

  const changeQty = (id: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((c) => c.product.id === id ? { ...c, qty: c.qty + delta } : c)
        .filter((c) => c.qty > 0)
    );
  };

  const subtotal = cart.reduce((s, c) => s + c.product.price * c.qty, 0);
  const totalItems = cart.reduce((s, c) => s + c.qty, 0);

  return (
    <div className={styles.stepContent}>
      {/* Scanner placeholder */}
      <div className={styles.scannerBox}>
        <svg className={styles.scannerIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>
        <p className={styles.scannerText}>Tap to scan UPC of Product</p>
        <p className={styles.scannerSub}>Point device camera at barcode</p>
      </div>

      <div className={styles.dividerLine}><span>or enter manually</span></div>

      {/* Select Products */}
      <div className={styles.sectionRow}>
        <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>
        <h3 className={styles.sectionTitle}>Select Products</h3>
      </div>

      {/* Category pills */}
      <p className={styles.catLabel}>Category</p>
      <div className={styles.pills}>
        {categories.map((c) => (
          <button
            key={c}
            className={`${styles.pill} ${activeCategory === c ? styles.pillActive : ''}`}
            onClick={() => setActiveCategory(c)}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className={styles.searchBox}>
        <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
        </svg>
        <input
          className={styles.searchInput}
          placeholder="Search products in this category..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Product list */}
      <div className={styles.productList}>
        {filtered.map((p) => {
          const qty = getQty(p.id);
          return (
            <div key={p.id} className={styles.productCard}>
              <div className={styles.productInfo}>
                <p className={styles.productName}>{p.name}</p>
                <p className={styles.productCat}>{p.category}</p>
                <div className={styles.productPriceRow}>
                  <span className={styles.productPrice}>${p.price.toFixed(2)}</span>
                  <span className={styles.productStock}>{p.stock} in stock</span>
                </div>
              </div>
              {qty > 0 ? (
                <div className={styles.qtyControl}>
                  <button className={styles.qtyBtn} onClick={() => changeQty(p.id, -1)}>−</button>
                  <span className={styles.qtyValue}>{qty}</span>
                  <button className={styles.qtyBtn} onClick={() => changeQty(p.id, 1)}>+</button>
                </div>
              ) : (
                <button className={styles.addBtn} onClick={() => addToCart(p)}>+ Add</button>
              )}
            </div>
          );
        })}
      </div>

      {/* Cart Summary (floating) */}
      {totalItems > 0 && (
        <div className={styles.cartSummary}>
          <div className={styles.cartHeader}>
            <svg className={styles.cartIcon} viewBox="0 0 24 24" fill="currentColor"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/></svg>
            <span className={styles.cartTitle}>Cart Summary</span>
            <span className={styles.cartBadge}>{totalItems}</span>
          </div>
          <div className={styles.cartRow}><span>Items</span><span>{totalItems} items</span></div>
          <div className={styles.cartRow}><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          <div className={styles.cartRow}><span>Est. Margin</span><span className={styles.cartMargin}>18.5%</span></div>
          <div className={styles.cartTotal}><span>Total</span><span className={styles.cartTotalVal}>${subtotal.toFixed(2)}</span></div>
        </div>
      )}
    </div>
  );
};

/* ───── Step 1: Customer ───── */
const StepCustomer: React.FC<{
  selected: CustomerOption | null;
  setSelected: (c: CustomerOption) => void;
}> = ({ selected, setSelected }) => {
  const [search, setSearch] = useState('');

  const customers = useCustomersStore((s) => s.items);
  const fetchCustomers = useCustomersStore((s) => s.fetchList);
  useEffect(() => {
    if (customers.length === 0) fetchCustomers().catch(() => undefined);
  }, [customers.length, fetchCustomers]);

  const customerOptions = useMemo(() => customers.map(customerToOption), [customers]);
  const filtered = customerOptions.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={styles.stepContent}>
      <div className={styles.sectionRow}>
        <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
        </svg>
        <div>
          <h3 className={styles.sectionTitle}>Select Customer</h3>
          <p className={styles.sectionSub}>Choose who you're creating this order for</p>
        </div>
      </div>

      <div className={styles.searchBox}>
        <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
        </svg>
        <input className={styles.searchInput} placeholder="Search customers by name..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className={styles.customerList}>
        {filtered.map((c) => {
          const isSelected = selected?.id === c.id;
          const creditClass = c.creditStatus === 'Excellent' ? styles.creditExcellent : c.creditStatus === 'Good' ? styles.creditGood : styles.creditFair;
          return (
            <div
              key={c.id}
              className={`${styles.customerCard} ${isSelected ? styles.customerSelected : ''}`}
              onClick={() => setSelected(c)}
            >
              <div>
                <p className={styles.customerName}>
                  {c.name}
                  {isSelected && <svg className={styles.checkMark} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>}
                </p>
                <p className={styles.customerArea}>{c.area}</p>
                <p className={styles.creditRow}>Credit Status: <span className={creditClass}>{c.creditStatus}</span></p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ───── Step 2: Review ───── */
const StepReview: React.FC<{
  cart: CartItem[];
  customer: CustomerOption | null;
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
}> = ({ cart, customer, setCart }) => {
  const subtotal = cart.reduce((s, c) => s + c.product.price * c.qty, 0);
  const totalItems = cart.reduce((s, c) => s + c.qty, 0);

  const changeQty = (id: string, delta: number) => {
    setCart((prev) =>
      prev.map((c) => c.product.id === id ? { ...c, qty: Math.max(1, c.qty + delta) } : c)
    );
  };

  return (
    <div className={styles.stepContent}>
      <div className={styles.sectionRow}>
        <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10H7v-2h10v2z"/>
        </svg>
        <div>
          <h3 className={styles.sectionTitle}>Review Order</h3>
          <p className={styles.sectionSub}>Verify items and quantities before confirming</p>
        </div>
      </div>

      {/* Customer banner */}
      {customer && (
        <div className={styles.reviewCustomer}>
          <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor" style={{ color: '#2563EB' }}>
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
          </svg>
          <div>
            <p className={styles.reviewLabel}>Order for</p>
            <p className={styles.reviewCustomerName}>{customer.name}</p>
          </div>
        </div>
      )}

      {/* Cart items */}
      {cart.map((c) => (
        <div key={c.product.id} className={styles.reviewItem}>
          <div className={styles.reviewItemInfo}>
            <p className={styles.reviewItemName}>{c.product.name}</p>
            <p className={styles.reviewItemMeta}>${c.product.price.toFixed(2)} × {c.qty}</p>
            <p className={styles.reviewItemTotal}>${(c.product.price * c.qty).toFixed(2)}</p>
          </div>
          <div className={styles.qtyControl}>
            <button className={styles.qtyBtn} onClick={() => changeQty(c.product.id, -1)}>−</button>
            <span className={styles.qtyValue}>{c.qty}</span>
            <button className={styles.qtyBtn} onClick={() => changeQty(c.product.id, 1)}>+</button>
          </div>
        </div>
      ))}

      {/* Order Summary */}
      <div className={styles.orderSummaryCard}>
        <p className={styles.summaryTitle}>Order Summary</p>
        <div className={styles.summaryRow}><span>Subtotal ({totalItems} items)</span><span>${subtotal.toFixed(2)}</span></div>
        <div className={styles.summaryRow}><span>Estimated Margin</span><span className={styles.cartMargin}>18.5%</span></div>
        <div className={styles.summaryTotal}><span>Total Amount</span><span className={styles.summaryTotalVal}>${subtotal.toFixed(2)}</span></div>
      </div>
    </div>
  );
};

/* ───── Step 3: Confirm ───── */
const StepConfirm: React.FC<{
  cart: CartItem[];
  customer: CustomerOption | null;
}> = ({ cart, customer }) => {
  const subtotal = cart.reduce((s, c) => s + c.product.price * c.qty, 0);
  const totalItems = cart.reduce((s, c) => s + c.qty, 0);

  return (
    <div className={styles.stepContent}>
      {/* Big checkmark */}
      <div className={styles.confirmIcon}>
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
      </div>
      <h3 className={styles.confirmTitle}>Ready to Submit</h3>
      <p className={styles.confirmSub}>Review your order details one last time</p>

      {/* Customer Details */}
      {customer && (
        <div className={styles.confirmCard}>
          <div className={styles.confirmCardHeader}>
            <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
            <div>
              <p className={styles.confirmCardLabel}>Customer Details</p>
              <p className={styles.confirmCardValue}>{customer.name}</p>
              <p className={styles.confirmCardSub}>{customer.area}</p>
              <span className={styles.creditBadge}>Credit: {customer.creditStatus}</span>
            </div>
          </div>
        </div>
      )}

      {/* Order Summary */}
      <div className={styles.confirmCard}>
        <div className={styles.confirmCardHeader}>
          <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
          </svg>
          <p className={styles.confirmCardLabel}>Order Summary</p>
        </div>
        <div className={styles.summaryRow}><span>Total Items</span><span>{totalItems} items</span></div>
        <div className={styles.summaryRow}><span>Order Value</span><span>${subtotal.toFixed(2)}</span></div>
        <div className={styles.summaryRow}><span>Estimated Margin</span><span className={styles.cartMargin}>18.5%</span></div>
        <div className={styles.summaryTotal}><span>Total Amount</span><span className={styles.confirmTotalVal}>${subtotal.toFixed(2)}</span></div>
      </div>

      {/* Delivery */}
      <div className={styles.confirmCard}>
        <div className={styles.confirmCardHeader}>
          <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
          </svg>
          <div>
            <p className={styles.confirmCardLabel}>Delivery Information</p>
            <p className={styles.confirmCardSub}>Expected delivery in 3-5 business days</p>
          </div>
        </div>
      </div>

      {/* Notice */}
      <div className={styles.noticeCard}>
        <svg className={styles.noticeIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <div>
          <p className={styles.noticeTitle}>Important Notice</p>
          <p className={styles.noticeText}>This order will be submitted for approval. You'll receive a confirmation notification once it's been processed by the system.</p>
        </div>
      </div>
    </div>
  );
};

/* ───── Main Modal ───── */
export const CreateOrderModal: React.FC<CreateOrderModalProps> = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createOrder = useOrdersStore((s) => s.create);
  const fetchOrders = useOrdersStore((s) => s.fetchList);

  const handleClose = () => {
    setStep(0);
    setCart([]);
    setSelectedCustomer(null);
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedCustomer || cart.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await createOrder({
        customer_id: Number(selectedCustomer.id),
        items: cart.map((c) => ({
          sku_id: Number(c.product.id),
          quantity: c.qty,
          unit_price: c.product.price,
        })),
      });
      await fetchOrders();
      handleClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const canContinue = () => {
    if (step === 0) return cart.length > 0;
    if (step === 1) return selectedCustomer !== null;
    return true;
  };

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Stepper */}
        <OrderStepper currentStep={step} />

        {/* Step content */}
        <div className={styles.body}>
          {step === 0 && <StepProducts cart={cart} setCart={setCart} />}
          {step === 1 && <StepCustomer selected={selectedCustomer} setSelected={setSelectedCustomer} />}
          {step === 2 && <StepReview cart={cart} customer={selectedCustomer} setCart={setCart} />}
          {step === 3 && <StepConfirm cart={cart} customer={selectedCustomer} />}
          {submitError && (
            <div style={{ margin: '12px 0', padding: 12, background: '#FEF2F2', color: '#B91C1C', borderRadius: 6 }}>
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.backBtn} onClick={step === 0 ? handleClose : () => setStep(step - 1)}>
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          {step < 3 ? (
            <button
              className={styles.continueBtn}
              disabled={!canContinue()}
              onClick={() => setStep(step + 1)}
            >
              Continue
            </button>
          ) : (
            <button
              className={styles.submitBtn}
              onClick={handleSubmit}
              disabled={submitting}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              {submitting ? 'Submitting…' : 'Submit Order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
